import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import { customerSupportAdminService } from '../../services'
import ShopAutocomplete from './ShopAutocomplete'

/**
 * CS customer CSV import: pick shop, upload name+mobile CSV, dry-run then apply.
 * Write users can import; read users can view history and sample CSV.
 */
export default function CsCustomerImportPanel({
  canWrite,
  onError,
  onInfo,
  loading,
  setLoading,
}) {
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [reason, setReason] = useState('')
  const [file, setFile] = useState(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [preview, setPreview] = useState(null)
  const [actionId, setActionId] = useState('')
  const [applyResult, setApplyResult] = useState(null)
  const [history, setHistory] = useState([])

  const loadHistory = useCallback(async () => {
    setLoading(true)
    onError('')
    try {
      const data = await customerSupportAdminService.listActions({
        actionType: 'CUSTOMER_BULK_IMPORT',
        orgId: selectedOrg?.id ? Number(selectedOrg.id) : undefined,
        page: 1,
        limit: 15,
      })
      setHistory(data.items || [])
    } catch (err) {
      onError(
        err?.response?.data?.message ||
          err.message ||
          'Failed to load customer import history',
      )
    } finally {
      setLoading(false)
    }
  }, [selectedOrg, setLoading, onError])

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  const downloadSample = async () => {
    onError('')
    try {
      const blob =
        await customerSupportAdminService.downloadCustomerImportSample()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'cs-customer-import-sample.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      onError(
        err?.response?.data?.message ||
          err.message ||
          'Failed to download sample CSV',
      )
    }
  }

  const setCsvFile = (next) => {
    setFile(next)
    setPreview(null)
    setActionId('')
    setApplyResult(null)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    if (!canWrite) return
    const dropped = e.dataTransfer.files?.[0]
    if (dropped?.name?.toLowerCase().endsWith('.csv')) {
      setCsvFile(dropped)
    } else {
      onError('Please upload a CSV file only')
    }
  }

  const runDryRun = async () => {
    if (!selectedOrg?.id || !file) return
    if (reason.trim().length < 8) {
      onError('Reason must be at least 8 characters')
      return
    }
    setLoading(true)
    onError('')
    onInfo('')
    setApplyResult(null)
    try {
      const data = await customerSupportAdminService.customerImportDryRun({
        file,
        orgId: Number(selectedOrg.id),
        reason: reason.trim(),
      })
      setPreview(data.preview || data)
      setActionId(data.action?.id || '')
      onInfo('Dry-run ready. Review the preview, then Apply if correct.')
      await loadHistory()
    } catch (err) {
      const msg = err?.response?.data?.message
      onError(
        Array.isArray(msg)
          ? msg.join(', ')
          : msg || err.message || 'Dry-run failed',
      )
    } finally {
      setLoading(false)
    }
  }

  const runApply = async () => {
    if (!actionId) return
    setLoading(true)
    onError('')
    onInfo('')
    try {
      const data =
        await customerSupportAdminService.customerImportApply(actionId)
      setApplyResult(data.result || data)
      onInfo(
        `Imported ${data.result?.created ?? 0} customer(s). Failed: ${data.result?.failed ?? 0}.`,
      )
      setPreview(null)
      setActionId('')
      setFile(null)
      await loadHistory()
    } catch (err) {
      const msg = err?.response?.data?.message
      onError(
        Array.isArray(msg)
          ? msg.join(', ')
          : msg || err.message || 'Apply failed',
      )
    } finally {
      setLoading(false)
    }
  }

  const counts = preview?.counts

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Customer CSV import
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the shop first. The CSV contains only customer name and
            mobile number. Existing mobiles for that shop are skipped.
          </Typography>

          <Stack spacing={2}>
            <ShopAutocomplete
              value={selectedOrg}
              onChange={(shop) => {
                setSelectedOrg(shop)
                setPreview(null)
                setActionId('')
                setApplyResult(null)
              }}
              helperText="Shop that will receive these customers"
              required
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => void downloadSample()}
              >
                Sample CSV
              </Button>
              <Button variant="outlined" onClick={() => void loadHistory()}>
                Refresh history
              </Button>
            </Stack>

            {!canWrite ? (
              <Alert severity="info">
                Read-only: you can view import history and download the sample
                CSV. Senior CS / Superuser can upload.
              </Alert>
            ) : (
              <>
                <TextField
                  label="Reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  helperText="Required (8+ characters). Stored on the CS audit action."
                />

                <Box
                  onDragOver={(e) => {
                    e.preventDefault()
                    setIsDragOver(true)
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleDrop}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragOver ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: isDragOver ? 'action.hover' : 'transparent',
                  }}
                >
                  <CloudUploadIcon
                    sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }}
                  />
                  <Typography variant="body1" gutterBottom>
                    Drag and drop customer CSV here
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Columns: customer_name (or name) and contact_number (or
                    phone / mobile). Do not put shop id in the file.
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={!selectedOrg}
                  >
                    Choose CSV
                    <input
                      type="file"
                      accept=".csv"
                      hidden
                      onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  {file ? (
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                      Selected: {file.name}
                    </Typography>
                  ) : null}
                </Box>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    disabled={
                      !selectedOrg ||
                      !file ||
                      reason.trim().length < 8 ||
                      loading
                    }
                    onClick={() => void runDryRun()}
                  >
                    Dry run
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={!actionId || loading}
                    onClick={() => void runApply()}
                  >
                    Apply import
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {counts ? (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Dry-run preview
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 2 }}
            >
              <Chip
                label={`To create: ${counts.toCreate}`}
                color="success"
                size="small"
              />
              <Chip
                label={`Already on shop: ${counts.skippedExisting}`}
                size="small"
              />
              <Chip
                label={`Invalid rows: ${counts.csvInvalid}`}
                color={counts.csvInvalid ? 'warning' : 'default'}
                size="small"
              />
            </Stack>
            {(preview.toCreateSample || []).length ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Sample rows to create
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Mobile</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.toCreateSample.map((r) => (
                      <TableRow key={`c-${r.row}`}>
                        <TableCell>{r.row}</TableCell>
                        <TableCell>{r.customer_name}</TableCell>
                        <TableCell>{r.contact_number}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : null}
            {(preview.invalidSample || []).length ? (
              <>
                <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                  Invalid rows (sample)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.invalidSample.map((r) => (
                      <TableRow key={`i-${r.row}`}>
                        <TableCell>{r.row}</TableCell>
                        <TableCell>{r.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {applyResult ? (
        <Alert severity="success">
          Created {applyResult.created || 0}; failed {applyResult.failed || 0};
          skipped (race) {applyResult.skippedRace || 0}.
        </Alert>
      ) : null}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Recent customer imports
          </Typography>
          {loading && !history.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Shop</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Requester</TableCell>
                  <TableCell>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.shop_name || row.org_id || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240, whiteSpace: 'pre-wrap' }}>
                      {row.reason || '—'}
                    </TableCell>
                    <TableCell>
                      {row.requested_by_email || row.requested_by}
                    </TableCell>
                    <TableCell>
                      {row.result_summary?.created != null
                        ? `+${row.result_summary.created}`
                        : row.dry_run_result?.counts?.toCreate != null
                          ? `dry-run ${row.dry_run_result.counts.toCreate}`
                          : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {!history.length ? (
                  <TableRow>
                    <TableCell colSpan={6}>No customer imports yet</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Stack>
  )
}
